/* eslint-disable camelcase */
import React, { FC, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Card, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { Backdrop, CircularProgress, Snackbar, Button } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getItemFromPath } from '../../utils/dataTools'
import { getDomainName } from '../../utils/envVariables'
import { uploadedBackgroundCheckVerificationAttachment } from '../../redux/actions/backgroundCheckVerification'
import { Link } from 'react-router-dom'
import Provider from '../../types/Provider'
import CryptoJS from 'crypto-js'
import request from 'superagent'
import config from '../../../package.json'
const { api_port } = config
import './index.css'

interface Attachment {
  backgroundCheckVerificationEmployeeAttachmentId: number;
  fileLocation: string;
  attachmentType: string;
}

interface FileUploadOneByOneProps {
  employeeId: number;
  employeeNumber: number;
  error: any;
  id: string;
  name: string;
  placeholder: string;
  uploadedFile: Array<any>;
  readonly: boolean;
  selectedProvider: Provider;
  urlDirectory: string;
  user: any;
  warningMessage?: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
}

const FileUploadOneByOne: FC<FileUploadOneByOneProps> = ({
  employeeId,
  employeeNumber,
  error,
  id,
  name,
  placeholder,
  uploadedFile,
  readonly,
  selectedProvider,
  urlDirectory,
  user,
  warningMessage,
  setFieldValue,
  setFieldTouched,
}: FileUploadOneByOneProps) => {
  const dispatch = useDispatch()
  const domainName = getDomainName()
  const [previousFiles, setPreviousFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const { protocol, hostname } = window.location
  const key = 'lakelandproviderportalinsertuserencryption'
  const apiUrl = api_port==='' ? `${protocol}//${hostname}/api` : `${protocol}//${hostname}:${api_port}/api`
  const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, key).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')
  const token = localStorage.getItem('token')!== null && localStorage.getItem('token')!=='undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''

  /** Sync previousFiles when parent updates attachments */
  useEffect(() => {
    if (uploadedFile) {
      setPreviousFiles(uploadedFile)
      setFieldValue(name, uploadedFile)
      setTimeout(() => setFieldTouched(name, true))
    }
  }, [uploadedFile]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Handle selecting a new file */
  const handleChangeFile = async (event: any) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      setUploading(true)
      const formData = new FormData()
      let fileName = name.includes('dhs') ? 'DHS_Background Check Document ' : ( name.includes('doj') ? 'DOJ Background Check Document ' : 'Background Information Disclosure ' )
      fileName = `${fileName}Employee ${employeeNumber}`
      const lastFileIndex = previousFiles.length
      const splitName = file.name.split('.')

      formData.append('eid', email.eid)
      formData.append('providerId', `${selectedProvider.provider_id}`)
      formData.append('file', file, `${fileName}_${lastFileIndex + 1}.${splitName[splitName.length - 1]}`)
      request
        .post(`${apiUrl}/backgroundCheckVerificationAttachment/${employeeId}`)
        .set('Accept', 'application/json')
        .set('access-token',token)
        .send(formData)
        .then((response) => {
          const uploadedAttachment = response.body
          dispatch(uploadedBackgroundCheckVerificationAttachment(uploadedAttachment))
          setSuccessMessage('Upload successfull!')
          setShowSuccess(true)
          // Add returned backend attachment
          const newFiles = [...previousFiles, { backgroundCheckVerificationEmployeeAttachmentId: uploadedAttachment.backgroundCheckVerificationEmployeeAttachmentId, fileLocation: uploadedAttachment.fileLocation}]
          setPreviousFiles(newFiles)
          setFieldValue(name, newFiles)
          setTimeout(() => setFieldTouched(name, true))
        })
        .catch((error) => {
          setShowError(true)
          if (error.response) {
            setErrorMessage(error.response.text)
          }
        })
    } catch (err) {
      setShowError(true)
      if (err.response) {
        setErrorMessage(err.response.text || 'Upload failed')
      }
    } finally {
      setUploading(false)
    }
  }

  /** Handle backend delete */
  const handleDelete = async (attachment: Attachment) => {
    try {
      request
        .delete(`${apiUrl}/backgroundCheckVerificationAttachment/${attachment.backgroundCheckVerificationEmployeeAttachmentId}`)
        .set('Accept', 'application/json')
        .set('access-token',token)
        .send({ eid: email.eid })
        .then((response) => {
          const deletedAttachment = response.body
          /*dispatch(uploadedBackgroundCheckVerificationAttachment(uploadedAttachment))*/
          setSuccessMessage('Deleted successfully!')
          setShowSuccess(true)
          // Delete returned backend attachment
          const newFiles = previousFiles.filter(
            (f) =>
              f.backgroundCheckVerificationEmployeeAttachmentId !=
              deletedAttachment.backgroundCheckVerificationEmployeeAttachmentId
          )
          setPreviousFiles(newFiles)
          setFieldValue(name, newFiles)
          setTimeout(() => setFieldTouched(name, true))
        })
        .catch((error) => {
          setShowError(true)
          if (error.response) {
            setErrorMessage(error.response.text)
          }
        })
    } catch (err) {
      setShowError(true)
      if (err.response) {
        setErrorMessage(err.response.text || 'Delete failed')
      }
    }
  }

  const handleHideError = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setShowError(false)
  }
  
  const handleHideSuccess = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setShowSuccess(false)
  }

  // Uploaded file preview
  const formLabel = 'Upload File(s)'

  return (
    <Card style={error ? { borderColor: '#dc3545', borderRadius: '0.25rem' } : { borderRadius: '0.25rem' }}>
      <Card.Body style={{ background: '#f7f7f7' }}>
        {
          !readonly && (
            <Row>
              <Form.Group as={Col} xs={12}>
                <Form.Label>{formLabel}</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder={placeholder}
                    value=''
                    disabled
                  />
                  <InputGroup.Append>
                    <label htmlFor={name} className='file-upload'>Locate file...</label>
                    <input 
                      id={name}
                      name={name}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={handleChangeFile}
                    />
                  </InputGroup.Append>
                </InputGroup>
                {
                  warningMessage && (
                    <Form.Text className='warning-text'>Maximum upload file size: 10MB</Form.Text>
                  )
                }
                {error ? (
                  <Form.Text className='form-error'>
                    {error}
                  </Form.Text>
                ) : null}
                <Backdrop open={uploading} style={{ zIndex: 1 }}>
                  <CircularProgress color='primary' />
                </Backdrop>
                <Snackbar open={showSuccess} autoHideDuration={6000} onClose={handleHideSuccess}>
                  <Alert onClose={handleHideSuccess} severity="success" elevation={6} variant="filled">
                    {successMessage}
                  </Alert>
                </Snackbar>
              </Form.Group>
              {
                showError && (
                  <Alert
                    severity="error"
                    style={{ width:'90%' }}
                    onClose={handleHideError}
                  >
                    <div style={{ width:'55%' }} dangerouslySetInnerHTML={{__html: errorMessage }} />
                  </Alert>
                )
              }
            </Row>
          )
        }
        {
          (previousFiles && previousFiles.length > 0) ? (
            <>
              <Form.Label>Uploaded Files</Form.Label>
              <Card>
                <Card.Body style={{ display: 'flex', flexFlow: 'wrap' }}>
                  {uploading ? (
                    <div style={{ width: '100%' }}>
                      <Button variant='outlined' style={{ margin: 10 }} disabled>
                        <div>Uploading File
                          <CircularProgress size={10} style={{ marginLeft: 10, color: 'darkGrey' }} />
                        </div>
                      </Button>
                    </div>
                  ) : null}
                  {
                    previousFiles && previousFiles.map((fileLoaded) => {
                      const fileLoadedName = getItemFromPath(fileLoaded.fileLocation, 'file')
                      const fileFolder = getItemFromPath(fileLoaded.fileLocation, 'folder')

                      return (
                        <div style={{ marginRight: 15 }} key={fileLoaded[id]}>
                          <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                          {
                            id.includes('backgroundCheck') ? (
                              <span style={{ fontSize: '0.9rem', margin: '0px 8px' }}>
                                {fileLoadedName}
                              </span>
                            ) : (
                              <Link
                                to='route'
                                className='ml-2 mr-2'
                                style={{ fontSize: '0.9rem', margin: '0px 8px' }}
                                onClick={(event) => {
                                  event.preventDefault()
                                  window.open(`${domainName}/${urlDirectory}/${fileFolder}/${fileLoadedName}`)
                                }}
                              >
                                {fileLoadedName}
                              </Link>
                            )
                          }
                          {
                            !readonly ? (
                              <span onClick={() => handleDelete(fileLoaded)}>
                                <FontAwesomeIcon icon={['fas', 'times-circle']} color='red' />
                              </span>
                            ) : (
                              <span>
                                <FontAwesomeIcon icon={['fas', 'times-circle']} color='grey' />
                              </span>
                            )
                          }
                        </div>
                      )}
                    )
                  }
                </Card.Body>
              </Card>
            </>
          ) : (
            readonly ? (
              <Card>
                <Card.Body style={{ padding: '0.5rem', display: 'flex' }}>
                  <span style={{ fontWeight: 'lighter', marginLeft: 10 }}>No Files Loaded</span>
                </Card.Body>
              </Card>
            ) : null
          )
        }
      </Card.Body>
    </Card>
  )
}

export default FileUploadOneByOne